import { create } from "zustand";
import { orderService } from "./orderService";
import { Order, CreateOrderInput, OrderStatus, OrderState } from "./types";

export const useOrderStore = create<OrderState>((set, get) => ({
  orders: [],
  currentOrder: null,
  isLoading: false,
  error: null,

  fetchOrder: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      const order = await orderService.getOrder(id);
      set({ currentOrder: order, isLoading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch order";
      set({
        isLoading: false,
        error: message,
      });
      throw new Error(message);
    }
  },

  fetchCustomerOrders: async (status: OrderStatus) => {
    set({ isLoading: true, error: null });

    try {
      const orders = await orderService.getCustomerOrders(status);
      set({ orders, isLoading: false });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch customer orders";
      set({
        isLoading: false,
        error: message,
      });
      throw new Error(message);
    }
  },

  fetchCustomerOrdersByStatuses: async (statuses: OrderStatus[]) => {
    set({ isLoading: true, error: null });

    try {
      const uniqueStatuses = Array.from(new Set(statuses));
      const ordersByStatus = await Promise.all(
        uniqueStatuses.map((status) => orderService.getCustomerOrders(status)),
      );

      const mergedOrders = ordersByStatus.flat();
      const uniqueOrdersMap = new Map<string, Order>();
      mergedOrders.forEach((order) => {
        uniqueOrdersMap.set(order.id, order);
      });

      set({ orders: Array.from(uniqueOrdersMap.values()), isLoading: false });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch customer orders";
      set({
        isLoading: false,
        error: message,
      });
      throw new Error(message);
    }
  },

  searchOrders: (searchText: string, deliveryDate: string) => {
    const orders = get().orders;
    const keyword = searchText.trim().toLowerCase();
    const selectedDate = deliveryDate.trim();

    return orders.filter((order) => {
      if (selectedDate) {
        return order.delivery_date === selectedDate;
      }

      return true;
    });
  },
  
  createOrder: async (input: CreateOrderInput) => {
    set({ isLoading: true, error: null });

    try {
      const order = await orderService.createOrder(input);
      set((state) => ({
        orders: [...state.orders, order],
        isLoading: false,
      }));
      return order;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create order";
      set({
        isLoading: false,
        error: message,
      });
      throw new Error(message);
    }
  },

  approveOrder: async (id: string) => {
    const order = await orderService.approveOrder(id);
    set({ currentOrder: order });
    return order;
  },

  confirmPayment: async (id: string, paymentId: string | null) => {
    const order = await orderService.confirmPayment(id, paymentId);
    set({ currentOrder: order });
    return order;
  },

  reportOrderIssue: async (id: string, message: string) => {
    const order = await orderService.reportOrderIssue(id, message);
    set({ currentOrder: order });
    return order;
  },

  setDeliverySlot: async (id: string, slotId: string, date: string) => {
    const order = await orderService.setDeliverySlot(id, slotId, date);
    set({ currentOrder: order });
    return order;
  },

  clearError: () => {
    set({ error: null });
  },
}));
